import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import s from "../modules/MySubs.module.css";
import { useT, useI18nReady } from "./TextProvider";

// ===== helpers: auth
function getStoredUser() {
  const ls = window.localStorage.getItem("auth:user");
  const ss = window.sessionStorage.getItem("auth:user");
  const raw = ls ?? ss;
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}

function getAnyTokenFromStorage() {
  const pick = (s) =>
    s.getItem("auth:access") ||
    s.getItem("auth:token") ||
    s.getItem("auth:key") ||
    s.getItem("auth:jwt") ||
    s.getItem("auth:auth_token") ||
    null;
  return pick(window.localStorage) || pick(window.sessionStorage);
}

function makeAuthHeader(tokenLike) {
  if (!tokenLike) return {};
  const token = String(tokenLike);
  const forcedScheme = process.env.REACT_APP_AUTH_SCHEME;
  if (forcedScheme) return { Authorization: `${forcedScheme} ${token}` };
  const looksLikeJwt = token.startsWith("eyJ");
  return looksLikeJwt ? { Authorization: `Bearer ${token}` } : { Authorization: `Token ${token}` };
}

// ===== API endpoints
const YOZMA_SAVOLLAR_ENDPOINT =
  process.env.REACT_APP_YOZMA_SAVOLLAR_ENDPOINT ||
  "https://apitr.subxonqulov.uz/api/kurs-yozma-savollar/";

export default function MyWrittenQuestions() {
  const t = useT();
  const ready = useI18nReady();
  const navigate = useNavigate();
  const user = useMemo(() => getStoredUser(), []);

  const [allQuestions, setAllQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 8;

  // Загрузка вопросов
  useEffect(() => {
    let abort = false;
    (async () => {
      if (!user?.id) {
        setError("Foydalanuvchi topilmadi. Iltimos, qaytadan tizimga kiring.");
        setLoading(false);
        return;
      }
      setLoading(true);
      setError("");
      try {
        const token = getAnyTokenFromStorage();
        const res = await fetch(YOZMA_SAVOLLAR_ENDPOINT, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...(token ? makeAuthHeader(token) : {}),
          },
          credentials: "include",
        });

        if (!res.ok) {
          throw new Error("Savollarni yuklashda xatolik yuz berdi");
        }

        const data = await res.json().catch(() => []);
        let arr = Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : [];
        
        // НЕ фильтруем по пользователю - показываем ВСЕ вопросы
        if (!abort) setAllQuestions(arr);
      } catch (e) {
        if (!abort) setError(e?.message || "Savollarni yuklashda xatolik yuz berdi");
      } finally {
        if (!abort) setLoading(false);
      }
    })();
    return () => { abort = true; };
  }, [user?.id]);

  // Клиентская фильтрация
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return allQuestions;
    return allQuestions.filter((item) => {
      const savolText = String(item?.savol_uz || item?.savol_ru || item?.savol_en || "").toLowerCase();
      const kursName = String(item?.kurs?.name || "").toLowerCase();
      return savolText.includes(needle) || kursName.includes(needle);
    });
  }, [allQuestions, q]);

  // Пагинация
  const total = filtered.length;
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const pageClamped = Math.min(Math.max(1, page), pages);
  const slice = filtered.slice((pageClamped - 1) * pageSize, pageClamped * pageSize);

  useEffect(() => {
    if (pageClamped !== page) setPage(pageClamped);
  }, [pageClamped]);

  // Удаление вопроса
  async function handleDelete(id) {
    if (!window.confirm("Savolni o'chirmoqchisiz? Bu amal qaytarilib bo'lmaydi.")) return;
    try {
      const token = getAnyTokenFromStorage();
      const res = await fetch(`${YOZMA_SAVOLLAR_ENDPOINT}${id}/`, {
        method: "DELETE",
        headers: { ...(token ? makeAuthHeader(token) : {}) },
        credentials: "include",
      });
      if (!res.ok && res.status !== 204) throw new Error("Server o'chirishni rad etdi");
      setAllQuestions((prev) => prev.filter((x) => String(x.id) !== String(id)));
    } catch (e) {
      alert(e?.message || "O'chirish amalga oshmadi");
    }
  }

  // Очистка HTML тегов для отображения
  const stripHtml = (html) => {
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html || "";
    return tmp.textContent || tmp.innerText || "";
  };

  return (
    <div className={s.wrapper}>
      <div className={s.headRow}>
        <h1 className={s.title}>Yozma savollarim</h1>
        <button
          className={s.createNewBtn}
          onClick={() => navigate("/NewQuestion")}
          title="Yangi savol yaratish"
          aria-label="Yangi savol yaratish"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          Yangi savol
        </button>
      </div>

      <div className={s.toolbar}>
        <input
          className={s.search}
          placeholder="Savol yoki fan bo'yicha qidirish..."
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
        />
      </div>

      {loading && <div className={s.info}>Savollar yuklanmoqda…</div>}
      {error && !loading && <div className={s.error}>{error}</div>}

      {!loading && !error && (
        slice.length ? (
          <div className={s.grid}>
            {slice.map((item) => {
              const savolText = stripHtml(item?.savol_uz || item?.savol_ru || item?.savol_en || "");
              return (
                <article key={item.id} className={s.card}>
                  {/* <div className={s.thumbBox}>
                    {item.kurs?.img ? (
                      <img
                        src={item.kurs.img}
                        alt={item.kurs?.name}
                        className={s.thumb}
                        onError={(e) => {
                          e.target.src = "https://via.placeholder.com/150?text=No+Image";
                        }}
                      />
                    ) : (
                      <div className={s.thumbPlaceholder}>IMG</div>
                    )}
                  </div> */}
                  <div className={s.body}>
                    {/* <h3 className={s.name} title={item.kurs?.name}>
                      {item.kurs?.name || "No course"}
                    </h3> */}
                    <p className={s.excerpt}>
                      {savolText.slice(0, 80)}
                      {savolText.length > 80 ? "…" : ""}
                    </p>
                    <div className={s.meta}>
                      <span className={s.ai}>Savol #{item.tartib}</span>
                    </div>
                  </div>
                  <div className={s.row}>
                    <button
                      className={s.btn}
                      onClick={() => navigate(`/editQuestion/${item.id}`)}
                      title="Tahrirlash"
                      aria-label="Tahrirlash"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                      </svg>
                    </button>
                    <button
                      className={s.btn}
                      onClick={() => handleDelete(item.id)}
                      title="O'chirish"
                      aria-label="O'chirish"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1 2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        <line x1="10" y1="11" x2="10" y2="17"></line>
                        <line x1="14" y1="11" x2="14" y2="17"></line>
                      </svg>
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <div className={s.info}>Hozircha yozma savollar mavjud emas</div>
        )
      )}

      {/* Пагинация */}
      {!loading && !error && pages > 1 && (
        <div className={s.pagination} role="navigation" aria-label="pagination">
          <button
            className={s.pageBtn}
            disabled={pageClamped === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            ‹
          </button>
          <span className={s.pageInfo}>
            {pageClamped} / {pages}
          </span>
          <button
            className={s.pageBtn}
            disabled={pageClamped === pages}
            onClick={() => setPage((p) => Math.min(pages, p + 1))}
          >
            ›
          </button>
        </div>
      )}
    </div>
  );
}
